import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, User } from 'lucide-react';
import type { DeviceType, OpenTabSummary } from '@gc/shared';

import { OpenTabDialog } from '@/features/tabs/OpenTabDialog';

/**
 * Who an order is for (SPEC §5.4, simplified by owner request).
 *
 * Every order now goes to a party's tab. The old three-way choice — Tab,
 * Device, Table — asked the cashier to think in the data model's terms; the
 * cashier thinks in people. The server still accepts DEVICE/TABLE orders (the
 * portal and deep links use them), but staff pick a visitor, full stop.
 *
 * A device deep link (`?device=` from a tile's Order button) is resolved to
 * that machine's tab BEFORE this component renders, so it arrives here as a
 * pre-picked visitor rather than a separate destination type.
 */
export interface Destination {
  tabId: string | null;
  /** Shown on the collapsed control once picked; null until then. */
  tabName?: string | null;
  /** Where the party is sitting right now — device/table/cinema names. */
  locations?: string[];
  /**
   * The ONE machine this order was started from, when it was started from one.
   *
   * Set by the `?device=` deep link — a cashier pressing Order on PC-14's tile
   * — and it is what puts "PC-14" on the station ticket, so whoever carries the
   * drink knows where it goes. Null when the visitor was picked by name
   * instead: a party can be on six machines and no single one is the answer.
   */
  deviceId?: string | null;
  /**
   * That machine's type, because the destination it becomes is not the same
   * word for all of them: the server refuses `DEVICE` for a table and `TABLE`
   * for anything that is not one.
   */
  deviceType?: DeviceType | null;
}

/**
 * What the `?device=` deep link resolves to, given whatever the URL already
 * gave us.
 *
 * THE DEVICE IS RESOLVED EVEN WHEN THE TAB IS ALREADY KNOWN, and that is the
 * whole point of this function existing separately. The tile's Order button
 * links `?device=PC-14&tab=…` for any occupied machine — which is every order
 * anybody actually places — and an earlier version skipped the lookup whenever
 * a tab was present. The device was therefore dropped on exactly the path it
 * mattered on, and the chit printed "Tab" over a machine the cashier had
 * pressed by name.
 *
 * The URL's tab wins over the machine's own session: it is the party the
 * cashier pressed. The session is the fallback for a bare `?device=` link.
 */
export function resolveDeepLinkDestination(
  current: Destination,
  device: {
    id: string;
    name: string;
    deviceType: DeviceType;
    currentSession?: { tabId: string; tabName: string } | null;
  },
): Destination {
  const session = device.currentSession;
  return {
    tabId: current.tabId ?? session?.tabId ?? null,
    tabName: current.tabName ?? session?.tabName ?? null,
    locations: [device.name],
    // Set TOGETHER, always: `orderDestinationFor` reads the type to choose
    // between TABLE and DEVICE, and the server refuses the wrong one — so an id
    // without a type would send a table as a DEVICE and fail the whole order.
    deviceId: device.id,
    deviceType: device.deviceType,
  };
}

/**
 * Where an order is addressed, as the wire wants it.
 *
 * Staff orders used to go to the tab and nothing else, so a ticket for an order
 * placed from PC-14's own tile printed the party's name and no clue where they
 * were sitting — the runner had to ask. When the order started from ONE machine
 * it is addressed to that machine, and the ticket heads itself "PC-14".
 *
 * TABLE and DEVICE share one column and differ only in what the ticket calls
 * the place; the server refuses `DEVICE` for a table and `TABLE` for anything
 * else, so the word comes from the device's own type rather than a guess.
 */
export function orderDestinationFor(destination: Destination): {
  destinationType: 'TAB' | 'DEVICE' | 'TABLE';
  destinationDeviceId: string | null;
} {
  if (!destination.deviceId) {
    // Picked by NAME: says who is paying, not which machine it is for, and a
    // party on six PCs has no single one.
    return { destinationType: 'TAB', destinationDeviceId: null };
  }
  return {
    destinationType: destination.deviceType === 'TABLE' ? 'TABLE' : 'DEVICE',
    destinationDeviceId: destination.deviceId,
  };
}

interface DestinationPickerProps {
  value: Destination;
  onChange: (destination: Destination) => void;
}

export function DestinationPicker({ value, onChange }: DestinationPickerProps) {
  const { t } = useTranslation();
  // Closed until pressed — the picker is now a modal (the Outstanding screen's
  // pattern), and a dialog that opens itself on page load is an ambush.
  const [open, setOpen] = useState(false);

  const picked = value.tabId !== null;

  return (
    <div className="space-y-2">
      <span className="block text-xs text-content-secondary">{t('cafe.visitor')}</span>

      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-expanded={open}
        className={`flex w-full items-center gap-2 rounded-control px-2.5 py-2 text-start text-sm transition-colors ${
          picked ? 'border border-primary bg-primary/10 text-content' : 'bg-primary text-primary-fg'
        }`}
      >
        <User className="h-4 w-4 shrink-0" aria-hidden />
        {picked ? (
          <span className="min-w-0 flex-1">
            <span className="block truncate font-medium">{value.tabName}</span>
            {(value.locations?.length ?? 0) > 0 ? (
              <span className="flex items-center gap-1 text-[11px] text-content-muted">
                <MapPin className="h-3 w-3 shrink-0" aria-hidden />
                <span className="truncate">{value.locations!.join(' · ')}</span>
              </span>
            ) : null}
          </span>
        ) : (
          <span className="flex-1 font-medium">{t('cafe.visitor')}</span>
        )}
        <span className={`shrink-0 text-xs ${picked ? 'text-primary' : 'text-primary-fg/80'}`}>
          {picked ? t('cafe.changeVisitor') : t('cafe.chooseVisitor')}
        </span>
      </button>

      {open ? (
        <OpenTabDialog
          title={t('cafe.visitor')}
          // EVERY LIVE TAB: whoever owes money, is playing, holds a pass or was
          // opened moments ago — whatever day it started. This was narrowed to
          // parties present TODAY once, and that hid real people (somebody who
          // played last night, never paid and came back); the owner's call
          // then was "show all, and let staff pick". But "all" meant every
          // OPEN tab with a sitting on it, and a paid-up tab that never closed
          // is still OPEN: typing "Yaran staff" found a six-week-old tab that
          // owed nothing beside the real one owing 41,500 (owner, 2026-09-23:
          // "two tabs even tho in the outstanding bill there is only one").
          // The live list is the Outstanding screen's own rule, so the two
          // agree; each card says when it was opened and what it owes.
          source="live"
          value={value.tabId}
          onPick={(tabId, tab: OpenTabSummary) => {
            // No device: picking a NAME says who is paying, not which machine
            // the order is for, and a party on six PCs has no single one. The
            // ticket falls back to the tab name, as it always has.
            onChange({
              tabId,
              tabName: tab.name,
              locations: tab.locations ?? [],
              deviceId: null,
              deviceType: null,
            });
          }}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </div>
  );
}
